import type { JSX } from 'react';
import { useState } from 'react';
import {
  type PatientVisitDto,
  type TrialEnrollmentDto,
  type TrialEnrollmentWithPlanDto,
  type TrialVisitDto,
} from '@ctms/contracts';
import { visitLabel } from '@ctms/shared';

import { ENROLLMENT_STATUS_LABELS } from '../enrollment-status-labels.js';
import {
  ScheduleVisitDialog,
  type VisitWindowView,
} from './schedule-visit-dialog/schedule-visit-dialog.js';

/**
 * Contact channel of a scheduled visit (legacy `Patient_visit.Type`: Site,
 * Phone, Site_IMP, Home — `spec/legacy/frontend-v1.0.md:230`). The wording
 * mirrors the "Typ kontaktu" labels of the protocol catalog, so a visit reads
 * the same in both places.
 */
export const VISIT_TYPE_LABELS: Record<PatientVisitDto['type'], string> = {
  S: 'Ośrodek',
  P: 'Telefon',
  H: 'Dom',
  I: 'Ośrodek – wyłącznie podanie leku',
};

/**
 * Kit-registration flag of a visit (REQ-VIS-031: biała / czerwona / zielona).
 * The colour words come from `spec/legacy/frontend-v1.0.md:390` (R13).
 */
export const VISIT_FLAG_LABELS: Record<NonNullable<PatientVisitDto['flag']>, string> = {
  W: 'Biała',
  R: 'Czerwona',
  G: 'Zielona',
};

/** A participation of the chart plus the schedule plan that belongs to it. */
export interface ActiveParticipation {
  enrollment: TrialEnrollmentDto;
  plan: PatientVisitDto[];
}

/** One row of the visit table — the plan row enriched with the catalog name. */
export interface ScheduledVisitRow {
  id: number;
  /** Catalog position (`trial_visits.id`) — links the row to its protocol window (rework II.3). Null for a #41 template-preset row. */
  trialVisitId: number | null;
  /** REQ-PAT-024: the schedule points at the protocol catalog, so it renders as `V<n>` plus the two-part name (mail 6 D.5). */
  label: string;
  dateScheduled: string | null;
  timeScheduled: string | null;
  dateReal: string | null;
  type: PatientVisitDto['type'];
  flag: PatientVisitDto['flag'];
}

/**
 * The participation of the chart in the ACTIVE trial (element 6 of the Cockpit
 * legend). A person keeps one participation history; the bar shows the one that
 * belongs to the trial picked in the header.
 *
 * When the same person was enrolled in the same trial more than once, the
 * newest row wins (highest id — decision of session 21): one bar, consistent
 * with the participant list which sorts the newest participation first.
 */
export function selectParticipation(
  items: TrialEnrollmentWithPlanDto[],
  trialId: number,
): ActiveParticipation | null {
  let latest: TrialEnrollmentWithPlanDto | null = null;
  for (const item of items) {
    if (item.enrollment.trialId !== trialId) {
      continue;
    }
    if (latest === null || item.enrollment.id > latest.enrollment.id) {
      latest = item;
    }
  }
  if (latest === null) {
    return null;
  }
  return { enrollment: latest.enrollment, plan: latest.plan };
}

/**
 * Turns the stored plan into table rows in protocol order, naming each visit
 * after its catalog position (`trialVisitId → visitNumber` → `V<n>`, plus the
 * two-part visit name `Nazwa 1 (Nazwa 2)` when a second name exists, mail 6
 * D.5).
 *
 * A plan row that carries no catalog position still renders (never a blank
 * row): a #41 template preset shows its own `presetName`, anything else falls
 * back to a dash. Both sort to the end, so an unlinked row stays visible
 * instead of disappearing.
 */
export function buildVisitRows(plan: PatientVisitDto[], catalog: TrialVisitDto[]): ScheduledVisitRow[] {
  const positions = new Map(catalog.map((position) => [position.id, position]));
  return [...plan]
    .map((visit) => {
      const position = visit.trialVisitId === null ? undefined : positions.get(visit.trialVisitId);
      return {
        id: visit.id,
        trialVisitId: visit.trialVisitId,
        label: position
          ? `V${position.visitNumber} ${visitLabel(position.visitName1, position.visitName2)}`
          : (visit.presetName ?? '—'),
        order: position ? position.visitNumber : Number.MAX_SAFE_INTEGER,
        dateScheduled: visit.dateScheduled,
        timeScheduled: visit.timeScheduled,
        dateReal: visit.dateReal,
        type: visit.type,
        flag: visit.flag,
      };
    })
    .sort((left, right) => left.order - right.order || left.id - right.id);
}

/**
 * Adds days to an ISO day (`YYYY-MM-DD`) in UTC — no timezone/DST drift. The
 * catalog window is stored in day counts (REQ-TRIAL-009); the read-only
 * preview needs absolute days.
 */
export function addDaysIso(dateIso: string, days: number): string {
  const parts = dateIso.split('-').map(Number);
  const year = parts[0] ?? 0;
  const month = parts[1] ?? 1;
  const day = parts[2] ?? 1;
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10);
}

/**
 * Visit-window presentation for the read-only preview (rework II.3, Piotr's
 * mail 2026-10-10). The protocol catalog stores the window RELATIVE
 * (REQ-TRIAL-009: min/opt/max days from a referral visit; legacy dbase v1.2:
 * „okno wizyty w dniach od/do wizyty #Referral_visit"). The absolute range is
 * anchored to the referral visit's day IN THIS PATIENT'S PLAN: `dateReal` when
 * it was performed, else the planned day (anchor semantics approved by the
 * user 2026-10-11). No window in the catalog → `null` (the dialog renders
 * „bez okna"); window without a known referral day → relative description only.
 */
export function describeVisitWindow(
  position: TrialVisitDto | undefined,
  plan: PatientVisitDto[],
  catalog: TrialVisitDto[],
): VisitWindowView | null {
  if (
    position === undefined ||
    !position.window ||
    position.minDays === null ||
    position.optDays === null ||
    position.maxDays === null
  ) {
    return null;
  }
  const referral =
    position.referralVisitId === null
      ? undefined
      : catalog.find((row) => row.id === position.referralVisitId);
  const referralRow =
    referral === undefined ? undefined : plan.find((visit) => visit.trialVisitId === referral.id);
  const span = `min ${position.minDays} / opt ${position.optDays} / max ${position.maxDays} dni`;
  const referralLabel = referral === undefined ? '—' : `V${referral.visitNumber}`;
  const anchorDate =
    referralRow === undefined ? null : (referralRow.dateReal ?? referralRow.dateScheduled);
  if (anchorDate === null) {
    return {
      rangeText: null,
      detailText: `${span} od ${referralLabel} — brak daty wizyty odniesienia`,
    };
  }
  const anchorKind = referralRow?.dateReal != null ? 'wykonanie' : 'plan';
  return {
    rangeText: `${addDaysIso(anchorDate, position.minDays)} → ${addDaysIso(anchorDate, position.maxDays)}`,
    detailText: `od ${referralLabel} · ${anchorDate} (${anchorKind}) · ${span}`,
  };
}

function displayDate(value: string | null): string {
  return value == null || value.length === 0 ? '—' : value;
}

function displayScheduled(date: string | null, time: string | null): string {
  if (date == null || date.length === 0) return '—';
  return time ? `${date} ${time}` : date;
}

/**
 * P-11: the screening number leads, the randomization one follows and stays
 * hidden when absent — or when the trial card does not keep a randomization
 * number at all (B.1/B.2).
 */
function ParticipantNumber({
  enrollment,
  randomizationEnabled,
}: {
  enrollment: TrialEnrollmentDto;
  randomizationEnabled: boolean;
}): JSX.Element {
  return (
    <span className="flex items-baseline gap-1.5">
      <span className="font-medium text-slate-100">{enrollment.screeningNumber}</span>
      {!randomizationEnabled || enrollment.randomizationNumber === null ? null : (
        <span className="text-slate-400">{enrollment.randomizationNumber}</span>
      )}
    </span>
  );
}

export interface ChartParticipationBarProps {
  /** An active trial is picked in the cockpit header (REQ-VIS-005). */
  trialPicked: boolean;
  isLoading: boolean;
  isError: boolean;
  participation: ActiveParticipation | null;
  /** `patients:write` — enables the "Włącz do badania" enrollment action. */
  canEnroll?: boolean;
  /** Opens the enrollment dialog. */
  onEnroll?: () => void;
  /** Opens the qualification dialog of the active participation (issue #30). */
  onQualification?: () => void;
  /** B.1/B.2: whether the trial card keeps a randomization number (passed by the panel). */
  randomizationEnabled?: boolean;
  onRetry: () => void;
}

/**
 * Element 6 of the Cockpit legend: the chart bar of the selected person —
 * their participation in the ACTIVE trial (both numbers, status) instead of
 * the former placeholder.
 */
export function ChartParticipationBar({
  trialPicked,
  isLoading,
  isError,
  participation,
  canEnroll = false,
  onEnroll,
  onQualification,
  randomizationEnabled = false,
  onRetry,
}: ChartParticipationBarProps): JSX.Element {
  const showEnrollButton = canEnroll && onEnroll !== undefined;

  function renderEnrollButton(): JSX.Element | null {
    if (!showEnrollButton) {
      return null;
    }
    return (
      <button
        type="button"
        onClick={onEnroll}
        className="rounded border border-cyan-700/70 bg-cyan-950/40 px-2.5 py-1 text-xs font-medium text-cyan-300 hover:bg-cyan-900/60"
      >
        Włącz do badania
      </button>
    );
  }

  if (!trialPicked) {
    return (
      <div className="mt-3 flex items-center justify-between gap-2">
        <p role="status" className="text-xs text-slate-400">
          Wybierz badanie, aby zobaczyć udział pacjenta.
        </p>
        {renderEnrollButton()}
      </div>
    );
  }

  if (isLoading) {
    return (
      <p role="status" className="mt-3 text-xs text-slate-400">
        Ładowanie udziału pacjenta
      </p>
    );
  }

  if (isError) {
    return (
      <div role="alert" className="mt-3 rounded-md border border-rose-800/70 bg-rose-950/40 p-3">
        <p className="text-xs text-rose-200">Nie udało się wczytać udziału pacjenta.</p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-2 rounded-md border border-rose-700 px-3 py-1.5 text-xs text-rose-200"
        >
          Spróbuj ponownie
        </button>
      </div>
    );
  }

  if (participation === null) {
    return (
      <div className="mt-3 flex items-center justify-between gap-2">
        <p role="status" className="text-xs text-slate-400">
          Ten pacjent nie bierze udziału w wybranym badaniu.
        </p>
        {renderEnrollButton()}
      </div>
    );
  }

  return (
    <div className="mt-3 flex items-center justify-between gap-2">
      <p className="flex items-baseline gap-2 text-xs text-slate-200">
        <ParticipantNumber
          enrollment={participation.enrollment}
          randomizationEnabled={randomizationEnabled}
        />
        <span className="text-slate-500">·</span>
        <span>{ENROLLMENT_STATUS_LABELS[participation.enrollment.status]}</span>
      </p>
      <div className="flex items-center gap-2">
        {onQualification ? (
          <button
            type="button"
            onClick={onQualification}
            className="rounded border border-cyan-700/70 bg-cyan-950/40 px-2.5 py-1 text-xs font-medium text-cyan-300 hover:bg-cyan-900/60"
          >
            Kwalifikacja
          </button>
        ) : null}
        {renderEnrollButton()}
      </div>
    </div>
  );
}

export interface PatientVisitsTableProps {
  /** An active trial is picked in the cockpit header. */
  trialPicked: boolean;
  isLoading: boolean;
  isError: boolean;
  participation: ActiveParticipation | null;
  catalog: TrialVisitDto[];
  /** `visits:write` — without it the schedule is read-only (no "Umawiaj" button). */
  canWrite: boolean;
  /**
   * REQ-VIS-015: books the planned day of one visit. The panel owns the dialog
   * and invalidation (it is the only part knowing WHICH chart and trial are
   * open); the table only raises the event with the visit as the server stored
   * it. Without the callback the action stays hidden — same rule as the day
   * plan's `onRegisterVisit`.
   */
  onVisitScheduled?: (visit: PatientVisitDto) => void;
  /**
   * REQ-VIS-021: registers the performed side of one visit. The day plan is
   * unreachable for an unbooked visit (its rows need `date_scheduled`), so the
   * chart is THE entry for walk-in registrations. Same rule as the other
   * callbacks: the panel owns the window and the invalidation, the table only
   * raises the event with the visit as the server stored it; without the
   * callback the action stays hidden.
   */
  onRegisterVisit?: (visit: PatientVisitDto) => void;
  /** Opens the kit registration dialog for the visit (REQ-KIT-014..026). */
  onRegisterKits?: (visit: ScheduledVisitRow) => void;
  onRetry: () => void;
}

/**
 * Element 9 of the Cockpit legend: the real visit schedule of the selected
 * person, generated from the protocol catalog (REQ-PAT-024/025). The table
 * header (Wizyta | Plan | Wykonanie | Typ | Flaga) is unchanged — only the
 * body stops being a placeholder.
 *
 * The action column is the cockpit wireframe's `[ Umawiaj ](#schedule-visit)`
 * (REQ-VIS-015). It lives HERE, not in the day plan: a day-plan row exists only
 * once `date_scheduled` is set (the plan filters on an exact day), so the unscheduled
 * visit is reachable only from the chart.
 */
export function PatientVisitsTable({
  trialPicked,
  isLoading,
  isError,
  participation,
  catalog,
  canWrite,
  onVisitScheduled,
  onRegisterVisit,
  onRegisterKits,
  onRetry,
}: PatientVisitsTableProps): JSX.Element {
  const rows = participation === null ? [] : buildVisitRows(participation.plan, catalog);
  const [scheduleVisitId, setScheduleVisitId] = useState<number | null>(null);

  function placeholderRow(colSpan: number, message: string): JSX.Element {
    return (
      <tr>
        <td colSpan={colSpan} className="px-2 py-3 text-slate-500">
          {message}
        </td>
      </tr>
    );
  }

  const canSchedule = canWrite && onVisitScheduled !== undefined;
  // REQ-VIS-021: a visit already performed has no registration entry here -
  // the action serves visits that are still waiting (booked or never booked).
  const canRegister = canWrite && onRegisterVisit !== undefined;
  const hasActions = canSchedule || canRegister || Boolean(onRegisterKits);
  // The row behind the open dialog — its label and booked day feed the form.
  const scheduleVisit =
    scheduleVisitId === null ? undefined : rows.find((row) => row.id === scheduleVisitId);

  return (
    <>
      {isError ? (
        <div role="alert" className="mt-2 rounded-md border border-rose-800/70 bg-rose-950/40 p-3">
          <p className="text-xs text-rose-200">Nie udało się wczytać harmonogramu wizyt.</p>
          <button
            type="button"
            onClick={onRetry}
            className="mt-2 rounded-md border border-rose-700 px-3 py-1.5 text-xs text-rose-200"
          >
            Spróbuj ponownie
          </button>
        </div>
      ) : null}
      <table className="mt-3 w-full text-left text-xs">
        <thead>
          <tr className="border-b border-slate-800 text-slate-400">
            <th scope="col" className="px-2 py-2">
              Wizyta
            </th>
            <th scope="col" className="px-2 py-2">
              Plan
            </th>
            <th scope="col" className="px-2 py-2">
              Wykonanie
            </th>
            <th scope="col" className="px-2 py-2">
              Typ
            </th>
            <th scope="col" className="px-2 py-2">
              Flaga
            </th>
            {hasActions ? (
              <th scope="col" className="px-2 py-2">
                Akcje
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {!trialPicked
            ? placeholderRow(hasActions ? 6 : 5, 'Wybierz badanie, aby zobaczyć harmonogram wizyt.')
            : isLoading
              ? placeholderRow(hasActions ? 6 : 5, 'Ładowanie harmonogramu wizyt')
              : isError
                ? placeholderRow(hasActions ? 6 : 5, 'Harmonogram wizyt jest chwilowo niedostępny.')
                : participation === null
                  ? placeholderRow(
                      hasActions ? 6 : 5,
                      'Ten pacjent nie bierze udziału w wybranym badaniu.',
                    )
                  : rows.length === 0
                    ? placeholderRow(hasActions ? 6 : 5, 'Brak wizyt w harmonogramie.')
                    : rows.map((row) => (
                        <tr key={row.id} className="border-b border-slate-800/80">
                          <td className="px-2 py-2 text-slate-100">{row.label}</td>
                          <td className="px-2 py-2 text-slate-300">{displayScheduled(row.dateScheduled, row.timeScheduled)}</td>
                          <td className="px-2 py-2 text-slate-300">{displayDate(row.dateReal)}</td>
                          <td className="px-2 py-2 text-slate-300">{VISIT_TYPE_LABELS[row.type]}</td>
                          <td className="px-2 py-2 text-slate-300">
                            {onRegisterKits && row.flag !== null ? (
                              <button
                                type="button"
                                onClick={() => onRegisterKits(row)}
                                title={`Zarządzaj kitami dla wizyty ${row.label}`}
                                className="rounded px-1 py-0.5 hover:bg-slate-800 hover:text-cyan-300 transition-colors"
                              >
                                {VISIT_FLAG_LABELS[row.flag]}
                              </button>
                            ) : row.flag === null ? (
                              '—'
                            ) : (
                              VISIT_FLAG_LABELS[row.flag]
                            )}
                          </td>
                          {hasActions ? (
                            <td className="px-2 py-2">
                              <div className="flex items-center gap-1.5">
                                {/* Rework II.3 (mail Piotra 2026-10-10): the entry STAYS on a
                                    performed visit, but opens the dialog read-only (visit window
                                    + booked data preview); an editable booking still requires a
                                    visit that is waiting. The write gate is unchanged, and the
                                    API-side 409 for booking a performed visit stays as the
                                    second gate (`enrollments.service.ts`). */}
                                {canSchedule && (
                                  <button
                                    type="button"
                                    onClick={() => setScheduleVisitId(row.id)}
                                    title={
                                      row.dateReal === null
                                        ? `Umów wizytę ${row.label}`
                                        : `Podgląd wizyty ${row.label} (read-only)`
                                    }
                                    className="rounded border border-cyan-700/70 bg-cyan-950/40 px-2 py-1 text-[11px] font-medium text-cyan-300 hover:bg-cyan-900/60"
                                  >
                                    Umawiaj
                                  </button>
                                )}
                                {canRegister && row.dateReal === null && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const source = participation?.plan.find(
                                        (visit) => visit.id === row.id,
                                      );
                                      if (source !== undefined) {
                                        onRegisterVisit?.(source);
                                      }
                                    }}
                                    title={`Zarejestruj odbycie wizyty ${row.label}`}
                                    className="rounded border border-cyan-700/70 bg-cyan-950/40 px-2 py-1 text-[11px] font-medium text-cyan-300 hover:bg-cyan-900/60"
                                  >
                                    Rejestruj
                                  </button>
                                )}
                                {onRegisterKits && (
                                  <button
                                    type="button"
                                    onClick={() => onRegisterKits(row)}
                                    title={`Rejestruj kity dla wizyty ${row.label}`}
                                    className="rounded border border-emerald-700/70 bg-emerald-950/40 px-2 py-1 text-[11px] font-medium text-emerald-300 hover:bg-emerald-900/60"
                                  >
                                    Kity
                                  </button>
                                )}
                              </div>
                            </td>
                          ) : null}
                        </tr>
                      ))}
        </tbody>
      </table>
      {scheduleVisitId !== null && onVisitScheduled !== undefined ? (
        <ScheduleVisitDialog
          visitId={scheduleVisitId}
          visitLabel={scheduleVisit?.label ?? '—'}
          currentDate={scheduleVisit?.dateScheduled ?? null}
          currentTime={scheduleVisit?.timeScheduled ?? null}
          readOnly={scheduleVisit !== undefined && scheduleVisit.dateReal !== null}
          dateReal={scheduleVisit?.dateReal ?? null}
          windowInfo={
            scheduleVisit === undefined
              ? null
              : describeVisitWindow(
                  catalog.find((position) => position.id === scheduleVisit.trialVisitId),
                  participation?.plan ?? [],
                  catalog,
                )
          }
          onSaved={(visit) => {
            setScheduleVisitId(null);
            onVisitScheduled(visit);
          }}
          onClose={() => setScheduleVisitId(null)}
        />
      ) : null}
    </>
  );
}